/**
 * Curated retro palette for list color assignment.
 * Base colors are the visible swatches; soft/ink are generated from each base
 * so cards, icons, and text stay visually tied together.
 */
export type ListColorKey =
  | "grey"
  | "beige"
  | "brown"
  | "rust"
  | "clay"
  | "orange"
  | "gold"
  | "amber"
  | "yellow"
  | "lime"
  | "green"
  | "moss"
  | "forest"
  | "aqua"
  | "sky"
  | "blue"
  | "ocean"
  | "midnight"
  | "indigo"
  | "violet"
  | "purple"
  | "pink"
  | "red"
  | "plum"
  | "slate"
  | "graphite"
  | "black"
  | "ink"
  | "fresh"
  | "sage"
  | "rose"
  | "sand"
  | "cerise";

function retroColor(name: string, base: string, softStrength = 13) {
  return {
    name,
    base,
    soft: `color-mix(in srgb, ${base} ${softStrength}%, white)`,
    ink: `color-mix(in srgb, ${base} 70%, black)`,
  };
}

export const AREA_COLORS: Record<
  ListColorKey,
  { name: string; base: string; soft: string; ink: string }
> = {
  beige: retroColor("Beige", "#D7C692"),
  brown: retroColor("Brown", "#8B4D24"),
  rust: retroColor("Rust", "#9A3E12"),
  clay: retroColor("Clay", "#BE3A08"),
  orange: retroColor("Orange", "#CF6C00"),
  gold: retroColor("Gold", "#DA7F00"),
  amber: retroColor("Amber", "#ED9C00"),
  yellow: retroColor("Yellow", "#E9DAA3"),
  lime: retroColor("Lime", "#B9C779"),
  green: retroColor("Green", "#6E9B66"),
  moss: retroColor("Moss", "#7A874B"),
  forest: retroColor("Forest", "#3E7049"),
  aqua: retroColor("Aqua", "#92CDB8"),
  sky: retroColor("Sky Blue", "#1B9996"),
  blue: retroColor("Blue", "#0D6873"),
  ocean: retroColor("Ocean", "#117E83"),
  midnight: retroColor("Midnight", "#061D22", 11),
  indigo: retroColor("Indigo", "#253F55"),
  violet: retroColor("Violet", "#74486E"),
  purple: retroColor("Purple", "#5A304B"),
  plum: retroColor("Plum", "#6D2D45"),
  grey: retroColor("Grey", "#AEB1A7", 15),
  slate: retroColor("Slate", "#69716B"),
  graphite: retroColor("Graphite", "#313837", 10),
  black: retroColor("Black", "#020F13", 9),
  ink: retroColor("Ink", "#061D22", 10),
  fresh: retroColor("Fresh", "#92CDB8"),
  sage: retroColor("Sage", "#92CDB8"),
  rose: retroColor("Rose", "#A8232B"),
  sand: retroColor("Sand", "#E9DAA3"),
  pink: retroColor("Pink", "#A8232B"),
  red: retroColor("Red", "#B62217"),
  cerise: retroColor("Cerise", "#A8232B"),
};

export const COLOR_KEYS: ListColorKey[] = [
  "grey",
  "midnight",
  "blue",
  "sky",
  "aqua",
  "forest",
  "moss",
  "green",
  "lime",
  "yellow",
  "amber",
  "gold",
  "orange",
  "beige",
  "brown",
  "clay",
  "red",
  "pink",
  "violet",
  "purple",
  "black",
];
export const DEFAULT_AREA_COLOR: ListColorKey = "grey";

export function getListColor(key: string | null | undefined) {
  if (key && key in AREA_COLORS) return AREA_COLORS[key as ListColorKey];
  return AREA_COLORS[DEFAULT_AREA_COLOR];
}

function paletteKey(value: string | null | undefined): ListColorKey | null {
  return value && value in AREA_COLORS ? (value as ListColorKey) : null;
}

export function resolvePairedColorKeys(input: {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  fallbackPrimary?: string | null;
  fallbackSecondary?: string | null;
}): { primary: ListColorKey; secondary: ListColorKey } {
  const primary = paletteKey(input.primaryColor);
  const secondary = paletteKey(input.secondaryColor);
  const fallbackPrimary =
    paletteKey(input.fallbackPrimary) ??
    paletteKey(input.fallbackSecondary) ??
    DEFAULT_AREA_COLOR;
  const fallbackSecondary =
    paletteKey(input.fallbackSecondary) ??
    paletteKey(input.fallbackPrimary) ??
    DEFAULT_AREA_COLOR;

  return {
    primary: primary ?? secondary ?? fallbackPrimary,
    secondary: secondary ?? primary ?? fallbackSecondary,
  };
}

export function resolveInheritedListColorKeys(input: {
  primaryOverride?: string | null;
  secondaryOverride?: string | null;
  categoryPrimary?: string | null;
  categorySecondary?: string | null;
}): { primary: ListColorKey; secondary: ListColorKey } {
  const primaryOverride = paletteKey(input.primaryOverride);
  const secondaryOverride = paletteKey(input.secondaryOverride);
  const categoryPrimary = paletteKey(input.categoryPrimary);
  const categorySecondary = paletteKey(input.categorySecondary);
  const primary =
    primaryOverride ??
    categoryPrimary ??
    secondaryOverride ??
    categorySecondary ??
    DEFAULT_AREA_COLOR;
  const secondary =
    secondaryOverride ??
    categorySecondary ??
    primaryOverride ??
    categoryPrimary ??
    DEFAULT_AREA_COLOR;
  return { primary, secondary };
}

/**
 * Resolve the effective primary + secondary colors for a task instance.
 * Null means Auto: each side follows the other when possible, otherwise falls
 * back to the inherited list display or the first palette color.
 */
export function resolveTaskColors(input: {
  barColor?: string | null;
  bgColor?: string | null;
  listColor: string;
  listBgColor?: string | null;
}): { bar: string; soft: string; ink: string } {
  const resolved = resolvePairedColorKeys({
    primaryColor: input.barColor,
    secondaryColor: input.bgColor,
    fallbackPrimary: input.listColor,
    fallbackSecondary: input.listBgColor,
  });
  const bar = getListColor(resolved.primary);
  const soft = getListColor(resolved.secondary).soft;

  return { bar: bar.base, soft, ink: bar.ink };
}
